import { SagaIterator } from 'redux-saga';
import { all, call, put, takeLatest } from 'redux-saga/effects';
import {
  SIGN_UP,
  EMAIL_LOGIN,
  SignUpAction,
  EmailLoginAction,
  PasswordResetEmail,
  PASSWORD_RESET_EMAIL,
  ResetPassword,
  PASSWORD_RESET,
  FacebookLoginAction,
  GoogleLoginAction,
  FACEBOOK_LOGIN,
  GOOGLE_LOGIN,
  CHANGE_PASSWORD,
  ChangePassword,
} from './types';
import { MCAFirebaseSaga } from '../root-saga';
import { setLoading, setError } from '../app-globals/actions';
import { getUser, syncUser, setUserProvider } from '../user/actions';
import firebase from 'firebase/app';
import 'firebase/auth';

const firebaseFacebookProvider = new firebase.auth.FacebookAuthProvider();
const firebaseGoogleProvider = new firebase.auth.GoogleAuthProvider();

function* signUp({ accountType, user }: SignUpAction): SagaIterator<void> {
  try {
    yield put(setLoading(true, 'Signing you up'));
    const createdUser = yield call(
      MCAFirebaseSaga.auth.createUserWithEmailAndPassword,
      user.email,
      user.password
    );
    const { firstName, lastName, email, status } = user;
    const addUser = yield call(
      MCAFirebaseSaga.firestore.setDocument,
      `users/${createdUser.user.uid}`,
      {
        firstName,
        lastName,
        email,
        accountType,
        status,
        authComplete: false,
        userid: createdUser.user.uid,
      },
      { merge: false }
    );
    yield put(syncUser());
    yield put(setLoading(false, 'Account successfully created'));
    console.log(createdUser, accountType, addUser);
  } catch (e) {
    // console.log('Error signing up', e);
    yield put(setLoading(false, ''));
    yield put(setError(true, e.message));
  }
}

function* emailLogin({
  credentials,
  accountType,
}: EmailLoginAction): SagaIterator<void> {
  try {
    yield put(setLoading(true, 'Logging in'));
    const loginUser = yield call(
      MCAFirebaseSaga.auth.signInWithEmailAndPassword,
      credentials.email,
      credentials.password
    );
    const { additionalUserInfo } = loginUser;
    console.log(additionalUserInfo);
    yield put(getUser(loginUser.user.uid));
    yield put(setUserProvider(additionalUserInfo.providerId));
    yield put(setLoading(false, 'Login Successfully'));
  } catch (e) {
    console.log('Error logging in', e);
    yield put(setLoading(false, ''));
    let errorMessage;
    switch (e.code) {
      case 'auth/user-not-found':
        errorMessage = 'User not found';
        break;
      case 'auth/invalid-email':
        errorMessage = 'Invalid email';
        break;
      case 'auth/wrong-password':
        errorMessage = 'Password is incorrect';
        break;
      default:
        errorMessage = 'Something went wrong';
    }
    yield put(setError(true, errorMessage));
  }
}

function* passwordResetEmail({ email }: PasswordResetEmail) {
  try {
    yield put(setLoading(true, 'Sending email'));
    yield call(MCAFirebaseSaga.auth.sendPasswordResetEmail, email, {
      handleCodeInApp: false,
      url:
        'https://mca-dev-3954c.firebaseapp.com/authentication/login/email/:accountType',
    });

    yield put(setLoading(false, 'Email Sent'));
  } catch (e) {
    yield put(setError(true, e));
    yield put(setLoading(false, ''));
  }
}

function* resetPassword({ code, password }: ResetPassword) {
  try {
    yield put(setLoading(true, ''));
    yield call(MCAFirebaseSaga.auth.confirmPasswordReset, code, password);
    yield put(setLoading(false, 'Password updated'));
  } catch (e) {
    yield put(setError(true, e));
    yield put(setLoading(false, ''));
  }
}

function* facebooklogin({ accountType }: FacebookLoginAction) {
  try {
    const data = yield call(
      MCAFirebaseSaga.auth.signInWithPopup,
      firebaseFacebookProvider
    );
    const user = yield call(MCAFirebaseSaga.auth.signInWithCredential, data);
    const { additionalUserInfo } = user;
    const { profile } = additionalUserInfo;
    yield put(setUserProvider(additionalUserInfo.providerId));
    const loggedInUser = yield call(
      MCAFirebaseSaga.firestore.getDocument,
      `users/${user.user.uid}`
    );
    if (!loggedInUser.exists) {
      yield call(
        MCAFirebaseSaga.firestore.setDocument,
        `users/${user.user.uid}`,
        {
          firstName: profile.first_name,
          lastName: profile.last_name,
          email: profile.email,
          accountType,
          status: 'active',
          authComplete: false,
          userid: user.user.uid,
        },
        { merge: false }
      );
      yield put(getUser(user.user.uid));
      yield put(setLoading(false, 'Account successfully created'));
      return;
    }
    yield put(getUser(user.user.uid));
    // yield put(syncUser());
    // console.log('user finish sign in');
  } catch (error) {
    // console.log('error', error);
    yield put(setLoading(false, ''));
    yield put(setError(true, error.message));
  }
}

function* googleSignin({ accountType }: GoogleLoginAction) {
  try {
    const data = yield call(
      MCAFirebaseSaga.auth.signInWithPopup,
      firebaseGoogleProvider
    );
    const user = yield call(MCAFirebaseSaga.auth.signInWithCredential, data);
    const { additionalUserInfo } = user;
    const { profile } = additionalUserInfo;
    console.log(additionalUserInfo);
    yield put(setUserProvider(additionalUserInfo.providerId));
    const loggedInUser = yield call(
      MCAFirebaseSaga.firestore.getDocument,
      `users/${user.user.uid}`
    );
    if (!loggedInUser.exists) {
      yield call(
        MCAFirebaseSaga.firestore.setDocument,
        `users/${user.user.uid}`,
        {
          firstName: profile.given_name,
          lastName: profile.family_name,
          email: profile.email,
          accountType,
          status: 'active',
          authComplete: false,
          userid: user.user.uid,
        },
        { merge: false }
      );
      yield put(getUser(user.user.uid));
      yield put(setLoading(false, 'Account successfully created'));
      return;
    }
    yield put(getUser(user.user.uid));
    // yield put(syncUser());
    // console.log('user finish sign in');
  } catch (e) {
    yield put(setLoading(false, e.message));
  }
}

function* changeUserPassword({
  email,
  currentpassword,
  newpassword,
}: ChangePassword) {
  try {
    yield put(setLoading(true, 'Updating password'));
    let success = false;
    const reauth = async (currentpassword: string) => {
      const user = firebase.auth().currentUser;
      const cred = firebase.auth.EmailAuthProvider.credential(
        email,
        currentpassword
      );
      return await user?.reauthenticateWithCredential(cred);
    };
    const prom = () => {
      return reauth(currentpassword)
        ?.then(() => {
          const user = firebase.auth().currentUser;
          user
            ?.updatePassword(newpassword)
            .then(() => {
              success = true;
              return success;
            })
            .catch(err => {
              console.log('1st err', err);
              success = false;
              return success;
            });
          success = true;
          return success;
        })
        .catch(error => {
          console.log('2nd err', error);
          success = false;
          return success;
        });
    };

    const mess = yield call(prom);
    console.log('wew', mess);
    if (mess) {
      yield put(setLoading(false, 'Successfully password updated'));
      return;
    }
    yield put(setError(true, ''));
    yield put(setLoading(false, 'The current password is invalid.'));
  } catch (error) {
    yield put(setLoading(false, error.message));
    yield put(setError(true, error.message));
  }
}

export function* AuthSaga(): SagaIterator {
  yield all([
    takeLatest(SIGN_UP, signUp),
    takeLatest(EMAIL_LOGIN, emailLogin),
    takeLatest(PASSWORD_RESET_EMAIL, passwordResetEmail),
    takeLatest(PASSWORD_RESET, resetPassword),
    takeLatest(FACEBOOK_LOGIN, facebooklogin),
    takeLatest(GOOGLE_LOGIN, googleSignin),
    takeLatest(CHANGE_PASSWORD, changeUserPassword),
  ]);
}
